import React from "react";
import {
  Award,
  GraduationCap,
  MapPin,
  Mail,
  Globe,
  ArrowRight,
} from "lucide-react";

// Datos del Comité Científico
const comiteCientifico = [
  {
    nombre: "Alejandra Zúñiga Meneses",
    institucion: "Ministerio de Cultura del Perú",
    pais: "Perú",
    bandera: "🇵🇪",
    color: "bg-red-50 text-red-700 border-red-200",
  },
  {
    nombre: "Ana Flavia Machado",
    institucion: "Universidad Federal de Minas Gerais",
    pais: "Brasil",
    bandera: "🇧🇷",
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    nombre: "Andrea Báez Montenegro",
    institucion: "Universidad Austral de Chile",
    pais: "Chile",
    bandera: "🇨🇱",
    color: "bg-blue-50 text-blue-700 border-blue-200",
  },
  {
    nombre: "Luis César Herrero Prieto",
    institucion: "Universidad de Valladolid",
    pais: "España",
    bandera: "🇪🇸",
    color: "bg-amber-50 text-amber-700 border-amber-200",
  },
  {
    nombre: "Luis Fernando Aguado Quintero",
    institucion: "Pontificia Universidad Javeriana",
    pais: "Colombia",
    bandera: "🇨🇴",
    color: "bg-yellow-50 text-yellow-800 border-yellow-200",
  },
  {
    nombre: "Marcos Valdivia López",
    institucion: "Universidad Nacional Autónoma de México",
    pais: "México",
    bandera: "🇲🇽",
    color: "bg-green-50 text-green-700 border-green-200",
  },
  {
    nombre: "Nora Elena Espinal Monsalve",
    institucion: "Universidad Nacional de Colombia",
    pais: "Colombia",
    bandera: "🇨🇴",
    color: "bg-yellow-50 text-yellow-800 border-yellow-200",
  },
  {
    nombre: "Ronny Correa Quezada",
    institucion: "Universidad Técnica Particular de Loja",
    pais: "Ecuador",
    bandera: "🇪🇨",
    color: "bg-sky-50 text-sky-700 border-sky-200",
  },
  {
    nombre: "Pablo Cardoso Terán",
    institucion:
      "Facultad Latinoamericana de Ciencias Sociales FLACSO Sede Ecuador",
    pais: "Ecuador",
    bandera: "🇪🇨",
    color: "bg-sky-50 text-sky-700 border-sky-200",
  },
  {
    nombre: "Pedro Costa",
    institucion: "Instituto Universitario de Lisboa",
    pais: "Portugal",
    bandera: "🇵🇹",
    color: "bg-red-50 text-red-700 border-red-200",
  },
  {
    nombre: "Viviana Silvia Leonardi",
    institucion: "Universidad Nacional del Sur",
    pais: "Argentina",
    bandera: "🇦🇷",
    color: "bg-cyan-50 text-cyan-700 border-cyan-200",
  },
];

// Datos del Secretario Ejecutivo
const secretarioEjecutivo = {
  nombre: "Jonathan Daniel Gómez Zapata",
  institucion: "Universidad Nacional de Colombia",
  pais: "Colombia",
  bandera: "🇨🇴",
  rol: "Secretario Ejecutivo RIEC",
  correo: "jdgomez@unal.edu.co", // Opcional / ilustrativo
};

export default function Comite() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans antialiased py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Cabecera de la Sección */}
        <div className="text-center mb-12">
          <h1 className="text-center sm:text-4xl font-black tracking-tight text-slate-950">
            Comité Científico
          </h1>
        </div>

        {/* Sección de Destacado: Secretario Ejecutivo */}
        <div className="mb-10">
          <div className="relative overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50/50 to-white p-6 sm:p-8 shadow-sm">
            {/* Elemento de diseño de fondo */}
            <div className="absolute right-0 bottom-0 translate-x-4 translate-y-4 opacity-5 text-indigo-600">
              <Award className="w-40 h-40" />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 relative z-10">
              <div className="space-y-2">
                <span className="inline-block px-2.5 py-0.5 rounded-md text-[11px] font-bold tracking-wide uppercase bg-indigo-600 text-white">
                  {secretarioEjecutivo.rol}
                </span>
                <h3 className="text-xl font-bold tracking-tight text-slate-900">
                  {secretarioEjecutivo.nombre}
                </h3>
                <div className="flex flex-wrap items-center gap-y-1 gap-x-3 text-sm text-slate-500">
                  <span className="flex items-center gap-1.5 font-medium">
                    <GraduationCap className="w-4 h-4 text-slate-400" />
                    {secretarioEjecutivo.institucion}
                  </span>
                  <span className="hidden sm:inline text-slate-300">•</span>
                  <span className="flex items-center gap-1.5">
                    <span className="text-base">
                      {secretarioEjecutivo.bandera}
                    </span>
                    {secretarioEjecutivo.pais}
                  </span>
                </div>
              </div>

              {/* Botón rápido de acción para el Secretario */}
              {secretarioEjecutivo.correo && (
                <a
                  href={`mailto:${secretarioEjecutivo.correo}`}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-950 hover:border-slate-300 transition-all shadow-sm"
                >
                  <Mail className="w-3.5 h-3.5 text-slate-400" />
                  <span>siec2026@utpl.edu.ec </span>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Sección: Miembros del Comité en Formato de Lista */}
        <div>
          <div className="flex items-center justify-between mb-4 pl-1">
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400">
              Miembros Académicos
            </h2>
            <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2.5 py-0.5 rounded-full">
              {comiteCientifico.length} Miembros
            </span>
          </div>

          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden divide-y divide-slate-100">
            {comiteCientifico.map((miembro, index) => (
              <div
                key={index}
                className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50/70 transition-colors group"
              >
                {/* Nombre y Universidad */}
                <div className="flex items-start gap-4">
                  {/* Círculo indicador con número o icono */}
                  <div className="hidden sm:flex w-8 h-8 rounded-full bg-slate-50 border border-slate-150 items-center justify-center text-xs font-bold text-slate-400 group-hover:bg-indigo-50 group-hover:text-indigo-650 group-hover:border-indigo-150 transition-colors shrink-0">
                    {index + 1}
                  </div>

                  <div className="space-y-1">
                    <h4 className="font-bold text-base text-slate-900 group-hover:text-indigo-650 transition-colors">
                      {miembro.nombre}
                    </h4>
                    <p className="text-xs sm:text-sm text-slate-500 flex items-center gap-1.5 leading-relaxed">
                      <GraduationCap className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span>{miembro.institucion}</span>
                    </p>
                  </div>
                </div>

                {/* País / Distintivo */}
                <div className="flex items-center sm:justify-end shrink-0 pl-1 sm:pl-0">
                  <div
                    className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-bold tracking-wide ${miembro.color}`}
                  >
                    <span className="text-base leading-none">
                      {miembro.bandera}
                    </span>
                    <span>{miembro.pais}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer Informativo */}
        <div className="mt-12 text-center text-xs text-slate-400">
          <p>
            © 2026 Encuentro Científico Internacional • Red de Investigadores y
            Evaluadores de la Ciencia (RIEC)
          </p>
        </div>
      </div>
    </div>
  );
}
